import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaChevronDown,
  FaChevronRight,
  FaCloudUploadAlt,
  FaFile,
  FaFileExcel,
  FaFileImage,
  FaFilePdf,
  FaFileWord,
  FaFilter,
  FaImage,
  FaInfoCircle,
  FaPaperclip,
  FaPaperPlane,
  FaPlay,
  FaPlus,
  FaSearch,
  FaTimes,
  FaTrash,
  FaUsers,
  FaVideo,
} from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";
import FullPageSpinner from "../../components/FullPageSpinner";
import { useNavigate } from "react-router-dom";
import { useModal } from "../../context/ConfirmModalContext";
import { useToast } from "../../context/ToastContext";
import { sendRequest } from "../../utils/api";
import Site from "../../models/Site";
import User from "../../models/User";
import { capitalizeWords } from "../../utils/string";
import { getUserAvatar, isCeo, isMd } from "../../utils/user-helper";
import Attachment from "../../models/Attachment";
import Report from "../../models/Report";
import { formatDateTime } from "../../utils/date";
import Pagination from "../../components/Pagination";
import SearchFilterBar from "../../Modal/SearchFilterBar";
import FilterModal from "../../Modal/FilterModal";
import { AttachmentType } from "../../types/attachment";
import { useAttachments } from "../../context/Attachments";
import FilePreviewModal from "../../Modal/AttachmentPreview";
import { PROJECT_REPORT_CATEGORY } from "../../constants";
import ProfileAvatar from "../../components/ProfileAvatar";


export const ProjectReports = () => {

  const navigate = useNavigate();
  const { openModal } = useModal();
  const { showToast } = useToast();
  const {
    attachments,
    handleFileChange,
    removeAttachment,
    clearAttachments,
  } = useAttachments(5);


  const [activeView, setActiveView] = useState<
    "add" | "history"
  >("add");


  const [selectedProject, setSelectedProject] = useState<Site | null>({} as Site);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");

  const [projectOpen, setProjectOpen] = useState(false);

  const [description, setDescription] = useState("");

  const [users, setUsers] = useState<User[]>([]);
  const [sites, setSites] = useState<Site[]>([]);


  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  // FILTER STATE
  const [site, setSite] = useState("All Departments");
  const [category, setCategory] = useState("All Categories");
  const [showFilters, setShowFilters] = useState(false);

  const [draftSite, setDraftSite] = useState(site);
  const [draftCategory, setDraftCategory] = useState(category);
  const [visibilityDropdownOpen, setVisibilityDropdownOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");

  const [historySearch, setHistorySearch] = useState("");

  const [reports, setReports] = useState<Report[]>([]);
  const [historyProject, setHistoryProject] =
    useState("All Projects");
  const [historyDate, setHistoryDate] = useState("All Dates");
  // const [files, setFiles] = useState<Attachment[]>([]);
  const [viewerMedia, setViewerMedia] = useState<Attachment | null>(
    null
  );

  const [loading, setLoading] = useState<boolean>(false);


  useEffect(() => {
    if (users.length === 0) return;

    setSelectedUsers((prev) => {
      if (prev.length > 0) return prev;

      const mdUser = users.find(
        (user) => isMd(user.portfolio));

      const ceoUser = users.find(
        (user) => isCeo(user.portfolio));

      return [
        ...(mdUser ? [mdUser] : []),
        ...(ceoUser ? [ceoUser] : []),
      ];
    });
  }, [users]);

  useEffect(() => {
    const getUserAndSite = async () => {
      try {
        setLoading(true);
        const sites = await sendRequest("GET", "/sites");
        setSites(sites);

        const users = await sendRequest("GET", "/users");
        setUsers(users);
      } catch (err: any) {

      } finally {
        setLoading(false);
      }
    }
    getUserAndSite();
  }, [])

  const filteredUsers = useMemo(() => {
    return users.filter(
      (user) =>
        user.firstName
          .toLowerCase()
          .includes(userSearch.toLowerCase()) ||
        user.lastName
          .toLowerCase()
          .includes(userSearch.toLowerCase()) ||
        user.email
          .toLowerCase()
          .includes(userSearch.toLowerCase())
    );
  }, [users, userSearch]);



  const openFilters = () => {
    // Load currently applied filters into the draft
    setDraftSite(site);
    setDraftCategory(category);

    setShowFilters(true);
  };

  const applyFilters = () => {
    setSite(draftSite);
    setCategory(draftCategory);

    setShowFilters(false);
  };

  const cancelFilters = () => {
    // Discard draft changes
    setDraftSite(site);
    setDraftCategory(category);

    setShowFilters(false);
  };

  const clearFilters = () => {
    setDraftSite("All Project Site");
    setDraftCategory("All Categories");
  };


  const reportFilterFields = [
    {
      key: "site",
      label: "Project Site",
      value: draftSite,
      onChange: setDraftSite,
      options: [
        "All Project Site",
        ...sites.map(site => capitalizeWords(site.name))
      ],
    },

    {
      key: "category",
      label: "Category",
      value: draftCategory,
      onChange: setDraftCategory,
      options: ["All Categories", ...PROJECT_REPORT_CATEGORY],
    },
  ];


  const toggleSelectedUser = (user: User) => {
    // Prevent deselecting MD or CEO
    if (isMd(user.portfolio))
      return alert("The Managing Director cannot be removed.");
    else if (isCeo(user.portfolio))
      return alert("The CEO cannot be removed.");

    setSelectedUsers((prev) =>
      prev.some((item) => item.id === user.id)
        ? prev.filter((item) => item.id !== user.id)
        : [...prev, user]
    );
  };

  const openReport = (report: Report) => {
    navigate(`/project-reports/${report.id}`, { state: report });
    // setActiveView("details");
  };

  const goToHistory = async () => {
    try {
      setLoading(true);
      const reports = await sendRequest("GET", "/project-report");
      setReports(reports);
      setActiveView("history");
    } catch (err: any) {

    } finally {
      setLoading(false);
    }
  };

  const goToAdd = () => {
    setActiveView("add");
  };

  const createProject = async () => {
    return openModal(
      "Report Submission",
      `Are you sure you want to submit this report? `
      + "This action is irreversible.",
      async () => {
        setLoading(true);
        try {

          const recipients = selectedUsers.map(user => user.id);
          const report = {
            id: uuidv4(),
            description,
            category: selectedCategory.toLocaleLowerCase(),
            siteId: selectedProject?.id,
          };

          const formData = new FormData();

          attachments.forEach((attachment) => {
            formData.append("attachments", attachment.file);
          });
          const data = {
            report,
            recipients,
          };

          formData.append("data", JSON.stringify(data));
          await sendRequest("POST", "/project-report", formData);

          showToast("Project report submitted successfully.", {
            type: "success",
          });

          setSelectedProject(null);
          setDescription("");
          clearAttachments();
          setSelectedUsers(
            prev => prev.filter(user => isCeo(user.portfolio) ||
              isMd(user.portfolio))
          );

        } catch (err: any) {
          showToast(
            "Fail to submit project report",
            { type: "error" }
          );
          console.log(err.response)
        } finally {
          setLoading(false);
        }
      },
      true, "Yes, Submit", "Cancel",
    );
  }

  const filteredReports = useMemo(() => {
    return reports.filter((report) => {
      const search = historySearch.toLowerCase();

      const matchesSearch =
        report.site?.name?.toLowerCase().includes(search) ||
        report.user?.firstName?.toLowerCase().includes(search) ||
        report.description?.toLowerCase().includes(search);

      const matchesProject =
        historyProject === "All Projects" ||
        report.site.name.toLocaleLowerCase() === historyProject.toLowerCase();

      const matchesCategory =
        category === "All Categories" ||
        report.category?.toLocaleLowerCase() === category.toLowerCase();

      const matchesDate =
        historyDate === "All Dates" ||
        report.date === historyDate;

      return (
        matchesSearch &&
        matchesProject &&
        matchesCategory &&
        matchesDate
      );
    });
  }, [reports, historySearch, historyProject, category]);

  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);

  const paginatedReport = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    return filteredReports.slice(startIndex, endIndex);
  }, [filteredReports, page, itemsPerPage]);

  const getFile = (type: AttachmentType, files: Attachment[]) => {
    const fileList = (files ?? []).filter(file => file?.type?.includes(type));
    return fileList;
  }

  return (
    <div className="min-h-screen bg-slate-50">

      <FullPageSpinner show={loading} />
      <main className="mx-auto max-w-[1200px] px-4 py-6 ">


        <div className="mb-7">

          <div className="flex flex-col justify-between gap-5 md:flex-row md:items-end">

            <div>

              <h2
                // className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl"
                className="text-2xl font-bold text-slate-800"
              >
                {activeView === "add"
                  ? "Submit Report"
                  : "Report History"}
              </h2>
            </div>

            {/* ADD / HISTORY */}
            <div className="w-fit flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">

              <button
                onClick={goToAdd}
                className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition ${activeView === "add"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-slate-500 hover:bg-slate-50"
                  }`}
              >
                {FaPlus({ className: "text-xs" })}
                Add Report
              </button>

              <button
                onClick={goToHistory}
                className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition ${activeView === "history"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-slate-500 hover:bg-slate-50"
                  }`}
              >
                {FaFileImage({ className: "text-xs" })}
                History
              </button>
            </div>
          </div>
        </div>

        {/* ================================================= */}
        {/* ADD REPORT */}
        {/* ================================================= */}
        {activeView === "add" && (
          <div className="mx-auto w-full max-w-5xl pb-10">

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">

              {/* HEADER */}
              <div className="border-b border-slate-100 px-6 py-6 md:px-8">

                <div className="flex items-center gap-4">

                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    {FaFileImage({ className: "text-lg" })}
                  </div>

                  <div>
                    <h2 className="text-lg font-bold text-slate-900">
                      New Progress Report
                    </h2>

                    <p className="mt-1 text-sm text-slate-400">
                      Record project progress and share updates with your team.
                    </p>
                  </div>

                </div>

              </div>


              {/* FORM */}
              <div className="space-y-9 p-6 md:p-8">
                {/* ===================================================== */}
                {/* PROJECT */}
                {/* ===================================================== */}

                {/* Project Site */}
                <section>

                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6">

                    {/* PROJECT SITE */}
                    <section>
                      <div className="mb-4">
                        <h3 className="text-sm font-bold text-slate-900">
                          Project Site
                        </h3>
                        {/* <p className="mt-1 text-xs text-slate-400">
                          Select the project site this report belongs to.
                        </p> */}
                      </div>

                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => {
                            setProjectOpen((prev) => !prev);
                            setCategoryOpen(false);
                          }}
                          className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-left text-sm font-medium text-slate-700 transition hover:border-blue-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-50"
                        >
                          <span className="truncate">
                            {capitalizeWords(selectedProject?.name || "") || (
                              <span className="text-slate-400">
                                Select a project site
                              </span>
                            )}
                          </span>

                          {FaChevronDown({
                            className: "ml-3 shrink-0 text-xs text-slate-400",
                          })}
                        </button>

                        {projectOpen && (
                          <div className="absolute left-0 right-0 top-full z-40 mt-2 max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl">
                            {sites.length > 0 ? (
                              sites.map((site) => (
                                <button
                                  type="button"
                                  key={site.id}
                                  onClick={() => {
                                    setSelectedProject(site);
                                    setProjectOpen(false);
                                  }}
                                  className={`w-full rounded-lg px-3 py-3 text-left text-sm capitalize transition hover:bg-blue-50 ${selectedProject?.id === site.id
                                    ? "bg-blue-50 font-semibold text-blue-600"
                                    : "text-slate-700"
                                    }`}
                                >
                                  {site.name}
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-4 text-center">
                                <p className="text-sm font-medium text-slate-600">
                                  No project site found
                                </p>

                                <p className="mt-1 text-xs text-slate-400">
                                  Create a project site to get started.
                                </p>

                                <button
                                  type="button"
                                  onClick={() => {
                                    setProjectOpen(false);
                                    navigate("/settings/sites/create");
                                  }}
                                  className="mt-3 inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
                                >
                                  Create New Project Site
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </section>

                    {/* REPORT CATEGORY */}
                    <section>
                      <div className="mb-4">
                        <h3 className="text-sm font-bold text-slate-900">
                          Report Category
                        </h3>
                        {/* <p className="mt-1 text-xs text-slate-400">
                          Choose the category that best describes this report.
                        </p> */}
                      </div>

                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => {
                            setCategoryOpen((prev) => !prev);
                            setProjectOpen(false);
                          }}
                          className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-left text-sm font-medium text-slate-700 transition hover:border-blue-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-50"
                        >
                          <span className="truncate">
                            {selectedCategory || (
                              <span className="text-slate-400">
                                Select report category
                              </span>
                            )}
                          </span>

                          {FaChevronDown({
                            className: "ml-3 shrink-0 text-xs text-slate-400",
                          })}
                        </button>

                        {categoryOpen && (
                          <div className="absolute left-0 right-0 top-full z-40 mt-2 max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl">
                            {PROJECT_REPORT_CATEGORY.map((category) => (
                              <button
                                type="button"
                                key={category}
                                onClick={() => {
                                  setSelectedCategory(category);
                                  setCategoryOpen(false);
                                }}
                                className={`w-full rounded-lg px-3 py-3 text-left text-sm transition hover:bg-blue-50 ${selectedCategory === category
                                  ? "bg-blue-50 font-semibold text-blue-600"
                                  : "text-slate-700"
                                  }`}
                              >
                                {category}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </section>

                  </div>

                </section>

                {/* DIVIDER */}
                <div className="border-t border-slate-100" />


                {/* ===================================================== */}
                {/* PROGRESS DETAILS */}
                {/* ===================================================== */}

                <section>

                  <div className="mb-4 flex items-start justify-between">

                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
                        Progress Details
                      </h3>

                      <p className="mt-1 text-xs text-slate-400">
                        Describe the work completed and the current project status.
                      </p>
                    </div>

                    <span className="text-xs text-slate-400">
                      {description.length}/1000
                    </span>

                  </div>


                  <textarea
                    value={description}
                    onChange={(e) =>
                      setDescription(e.target.value)
                    }
                    maxLength={1000}
                    rows={8}
                    placeholder="Describe the work completed, current progress, challenges, materials used, or anything else the project team should know..."
                    className="w-full resize-none rounded-xl border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-50"
                  />
                </section>


                {/* DIVIDER */}
                <div className="border-t border-slate-100" />
                {/* ===================================================== */}
                {/* SHARE WITH */}
                {/* ===================================================== */}

                <section>

                  <div className="mb-4">

                    <h3 className="text-sm font-bold text-slate-900">
                      Share With
                    </h3>

                    <p className="mt-1 text-xs leading-5 text-slate-400">
                      Select the people who should have access to this report.
                      They will also receive a notification when it is submitted.
                    </p>

                  </div>


                  <div className="relative">

                    {/* SELECTED USERS */}
                    <button
                      type="button"
                      onClick={() =>
                        setVisibilityDropdownOpen(
                          (prev) => !prev
                        )
                      }
                      className="flex min-h-[64px] w-full flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-2.5 text-left transition hover:border-blue-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-50"
                    >

                      {selectedUsers.length > 0 ? (
                        selectedUsers.map((user) => (
                          <span
                            key={user?.id}
                            className="flex items-center gap-2 rounded-lg bg-blue-50 py-1.5 pl-1.5 pr-2 text-xs font-semibold text-blue-700"
                          >

                            <img
                              src={getUserAvatar(user)}
                              className="h-6 w-6 rounded-full object-cover"
                              alt=""
                            />

                            <span className="max-w-[150px] truncate">
                              {user?.firstName}
                            </span>

                            <span
                              role="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleSelectedUser(user);
                              }}
                              className="cursor-pointer text-blue-400 transition hover:text-red-500"
                            >
                              {FaTimes({})}
                            </span>

                          </span>
                        ))
                      ) : (
                        <span className="px-1 text-sm text-slate-400">
                          Select people to share this report with
                        </span>
                      )}

                      <span className="ml-auto px-2">
                        {FaChevronDown({
                          className: "text-xs text-slate-400",
                        })}
                      </span>

                    </button>


                    {/* DROPDOWN */}
                    {visibilityDropdownOpen && (
                      <div className="absolute left-0 right-0 top-full z-40 mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl">

                        {/* SEARCH */}
                        <div className="border-b border-slate-100 p-3">

                          <div className="relative">

                            {FaSearch({
                              className:
                                "absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400",
                            })}

                            <input
                              value={userSearch}
                              onChange={(e) => setUserSearch(e.target.value)}
                              onClick={(e) =>
                                e.stopPropagation()
                              }
                              placeholder="Search people..."
                              className="w-full rounded-lg bg-slate-50 py-2.5 pl-9 pr-3 text-sm outline-none transition focus:bg-white focus:ring-2 focus:ring-blue-50"
                            />

                          </div>

                        </div>


                        {/* USER LIST */}
                        <div className="h-64 overflow-y-auto p-2">
                          {/* SELECT / CLEAR ALL */}
                          <div className="mb-2 flex items-center justify-between border-b border-slate-100 px-2 pb-2">
                            <span className="text-xs text-slate-400">
                              {selectedUsers.length} of {users.length} selected
                            </span>

                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedUsers((prev) => [
                                    ...prev,
                                    ...filteredUsers.filter(
                                      (user) =>
                                        !prev.some(
                                          (selected) => selected.id === user.id
                                        )
                                    ),
                                  ]);
                                }}
                                className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                              >
                                Select all
                              </button>

                              <button
                                type="button"
                                onClick={() => setSelectedUsers(
                                  users => users.filter(
                                    user => user.portfolio.toLocaleLowerCase() === "ceo" ||
                                      user.portfolio.toLocaleLowerCase() === "managing director" ||
                                      user.portfolio.toLocaleLowerCase() === "md"
                                  ))}
                                className="text-xs font-semibold text-slate-500 hover:text-red-500"
                              >
                                Clear all
                              </button>
                            </div>
                          </div>

                          {/* USERS — ONE PER ROW */}
                          {users.length > 0 ? (
                            <div className="grid grid-cols-1 gap-1">
                              {filteredUsers.map((user) => {
                                const selected = selectedUsers.some(
                                  (item) => item?.id === user?.id
                                );

                                return (
                                  <button
                                    type="button"
                                    key={user.id}
                                    onClick={() => toggleSelectedUser(user)}
                                    className={`flex w-full min-w-0 items-center gap-3 rounded-xl border p-3 text-left transition ${selected
                                      ? "border-blue-200 bg-blue-50/60"
                                      : "border-transparent hover:bg-slate-50"
                                      }`}
                                  >
                                    <img
                                      src={getUserAvatar(user)}
                                      className="h-10 w-10 shrink-0 rounded-full object-cover"
                                      alt=""
                                    />

                                    <div className="min-w-0 flex-1">
                                      <p className="truncate text-sm font-semibold text-slate-800">
                                        {`${user.firstName} ${user.lastName}`}
                                      </p>

                                      <p className={`
                                        mt-0.5 truncate text-xs text-slate-400 ${user.portfolio.toLocaleUpperCase() === "ceo" ? "uppercase" : "capitalize"}
                                        `}>
                                        {user.portfolio}
                                      </p>
                                    </div>

                                    <div
                                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-xs transition ${selected
                                        ? "border-blue-600 bg-blue-600 text-white"
                                        : "border-slate-300 bg-white"
                                        }`}
                                    >
                                      {selected && "✓"}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="px-4 py-8 text-center">
                              {FaUsers({
                                className: "mx-auto mb-2 text-xl text-slate-300",
                              })}

                              <p className="text-sm font-medium text-slate-500">
                                No users found
                              </p>
                            </div>
                          )}
                        </div>


                        {/* FOOTER */}
                        <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50 px-3 py-2.5">

                          <span className="text-xs text-slate-400">
                            {selectedUsers.length} selected
                          </span>

                          <button
                            type="button"
                            onClick={() =>
                              setVisibilityDropdownOpen(
                                false
                              )
                            }
                            className="rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-blue-700"
                          >
                            Done
                          </button>

                        </div>

                      </div>
                    )}

                  </div>


                  {/* INFO */}
                  <div className="mt-3 flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2.5">

                    {FaInfoCircle({
                      className:
                        "mt-0.5 shrink-0 text-xs text-slate-400",
                    })}

                    <p className="text-[11px] leading-5 text-slate-500">
                      MD and CEO are automatically included and will have access
                      to the report.
                    </p>

                  </div>

                </section>


                {/* DIVIDER */}
                <div className="border-t border-slate-100" />


                {/* ===================================================== */}
                {/* ATTACHMENTS */}
                {/* ===================================================== */}

                <section>

                  <div className="mb-4">

                    <h3 className="text-sm font-bold text-slate-900">
                      Attachments
                    </h3>

                    <p className="mt-1 text-xs text-slate-400">
                      Add photos, videos, or documents showing the current progress.
                    </p>

                  </div>


                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">

                    {/* ADD MEDIA */}
                    <label className="flex aspect-square cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 px-3 text-center transition hover:border-blue-400 hover:bg-blue-50">

                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-blue-500 shadow-sm">
                        {FaCloudUploadAlt({
                          className: "text-lg",
                        })}
                      </div>

                      <span className="mt-3 text-xs font-semibold text-slate-600">
                        Add Media
                      </span>

                      <span className="mt-1 text-[10px] text-slate-400">
                        Images, videos, or documents
                      </span>

                      <input
                        type="file"
                        multiple
                        accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      {/* 
                      <input
                        type="file"
                        multiple
                        accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
                        onClick={(e) => {
                          e.preventDefault();

                          openModal(
                            "🚀 Feature Coming Soon",
                            "Adding photos, videos, and documents is currently unavailable. This feature will be available soon.",
                            closeModal,
                            false,
                            "Close"
                          );
                        }}
                        className="hidden"
                      /> */}

                    </label>


                    {/* MEDIA */}
                    {attachments.map((item) => {
                      const isImage = item?.file.type?.includes("image");
                      const isVideo = item?.type === "video";
                      const isPdf = item?.file?.type.endsWith("pdf");
                      const isExcel = item?.file.type?.includes("spreadsheetml.sheet");
                      const isWord = item?.file.type.includes("wordprocessingml.document");

                      return (
                        <div
                          key={item.id}
                          className="group relative aspect-square overflow-hidden rounded-xl bg-slate-100"
                        >
                          {isImage ? (
                            <img
                              src={URL.createObjectURL(item.file)}
                              alt={item?.file.name || ""}
                              className="h-full w-full object-cover"
                            />
                          ) : isVideo ? (
                            <>
                              <video
                                src={URL.createObjectURL(item.file)}
                                className="h-full w-full object-cover"
                              />

                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-blue-600 shadow-sm">
                                  {FaPlay({
                                    className: "text-xs",
                                  })}
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="flex h-full w-full flex-col items-center justify-center p-4">
                              <div
                                className={`flex h-12 w-12 items-center justify-center rounded-xl text-xl ${isPdf
                                  ? "bg-red-50 text-red-600"
                                  : isExcel
                                    ? "bg-green-50 text-green-600"
                                    : isWord
                                      ? "bg-blue-50 text-blue-600"
                                      : "bg-slate-200 text-slate-600"
                                  }`}
                              >
                                {isPdf
                                  ? FaFilePdf({})
                                  : isExcel
                                    ? FaFileExcel({})
                                    : isWord
                                      ? FaFileWord({})
                                      : FaFile({})}
                              </div>

                              <p className="mt-3 w-full truncate text-center text-xs font-medium text-slate-700">
                                {item?.file?.name}
                              </p>

                              {/* <p className="mt-1 text-[10px] uppercase text-slate-400">
                                {item?.file.type}
                              </p> */}
                            </div>
                          )}

                          {/* Remove */}
                          <button
                            type="button"
                            onClick={() => removeAttachment(item.id)}
                            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-xs text-white opacity-0 transition group-hover:opacity-100 hover:bg-red-500"
                          >
                            {FaTrash({})}
                          </button>
                        </div>
                      );
                    })}

                  </div>

                </section>


                {/* ===================================================== */}
                {/* ACTIONS */}
                {/* ===================================================== */}

                <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-7 sm:flex-row sm:items-center sm:justify-between">

                  <p className="hidden text-xs text-slate-400 sm:block">
                    The report date will be recorded automatically.
                  </p>

                  <div className="flex flex-col-reverse gap-3 sm:flex-row">

                    <button
                      type="button"
                      onClick={() => {
                        setDescription("");
                        clearAttachments();
                        setSelectedProject(null);
                      }}
                      className="rounded-xl border border-slate-200 px-6 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
                    >
                      Clear
                    </button>

                    <button
                      type="button"
                      disabled={
                        !description.trim() ||
                        !selectedProject ||
                        selectedUsers.length === 0
                      }
                      onClick={createProject}
                      className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-7 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {FaPaperPlane({})}
                      Submit Report
                    </button>

                  </div>

                </div>

              </div>

            </div>

          </div>
        )}

        {/* ================================================= */}
        {/* HISTORY */}
        {/* ================================================= */}
        {activeView === "history" && (
          <div>

            {/* FILTER BAR */}
            <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-3 md:p-0 shadow-sm">

              <div className="flex flex-col gap-3">

                {reports.length > 0 && <div className="border-slate-200  sm:p-3">
                  <div className="relative">

                    <SearchFilterBar
                      search={historySearch}
                      onSearchChange={setHistorySearch}
                      placeholder="Search..."
                      onFilterClick={openFilters}
                      filterActive={
                        !site.toLocaleLowerCase().startsWith("all") ||
                        !category.toLocaleLowerCase().startsWith("all")
                      }
                    />

                    {/* FILTER POPOVER */}
                    <FilterModal
                      isOpen={showFilters}
                      title="Filter Project Report"
                      description="Refine the project reports displayed below"
                      fields={reportFilterFields}
                      onApply={applyFilters}
                      onCancel={cancelFilters}
                      onClear={clearFilters}
                    />
                  </div>
                </div>}

              </div>

            </div>

            {/* RESULT COUNT */}
            <div className="mb-4 flex items-center justify-between">

              <p className="text-sm text-slate-500">
                Showing{" "}
                <span className="font-semibold text-slate-800">
                  {filteredReports.length}
                </span>{" "}
                reports
              </p>

              <span className="text-xs text-slate-400">
                Latest first
              </span>

            </div>

            {/* REPORTS */}
            <div className="space-y-4">

              {paginatedReport.map((report) => (
                <button
                  key={report.id}
                  onClick={() => openReport(report)}
                  className="group w-full rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-md md:p-6"
                >

                  <div className="flex flex-col gap-5 md:flex-row">
                    {/* MEDIA */}

                    <div className="order-2 min-w-0 md:order-1">
                      {report.attachments?.length > 0 ? (
                        (() => {
                          const attachments = report.attachments;

                          const counts = {
                            images: attachments.filter((item) =>
                              item.file?.type?.endsWith("png") ||
                              item.file?.type?.endsWith("jpg")
                            ).length,
                            pdfs: attachments.filter((item) =>
                              item.file?.type?.includes("pdf")
                            ).length,
                            videos: attachments.filter((item) =>
                              item.file?.type?.startsWith("video")
                            ).length,
                            docs: attachments.filter((item) =>
                              item.file?.type?.includes("docx")
                            ).length,
                            excels: attachments.filter((item) =>
                              ["xls", "xlsx"].some((ext) =>
                                item.file?.name?.toLowerCase().endsWith(`.${ext}`)
                              )
                            ).length,
                          };

                          const items = [
                            { count: counts.images, label: "Images", icon: FaImage, color: "text-blue-600", bg: "bg-blue-50" },
                            { count: counts.pdfs, label: "PDFs", icon: FaFilePdf, color: "text-red-500", bg: "bg-red-50" },
                            { count: counts.videos, label: "Videos", icon: FaPlay, color: "text-purple-600", bg: "bg-purple-50" },
                            { count: counts.docs, label: "Docs", icon: FaFileWord, color: "text-indigo-600", bg: "bg-indigo-50" },
                            { count: counts.excels, label: "Excel", icon: FaFileExcel, color: "text-emerald-600", bg: "bg-emerald-50" },
                          ].filter((item) => item.count > 0);

                          return (
                            <>
                              {/* Desktop attachment summary */}
                              <div className="hidden md:flex w-full max-w-md items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                                  {FaPaperclip({ className: "text-lg" })}
                                </div>

                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center justify-between gap-3">
                                    <div>
                                      <p className="text-sm font-bold text-slate-800">
                                        Supporting documents
                                      </p>
                                      <p className="mt-0.5 text-xs text-slate-500">
                                        {attachments.length} files attached
                                      </p>
                                    </div>

                                    <span className="flex h-8 min-w-8 items-center justify-center rounded-lg bg-slate-100 px-2 text-sm font-bold text-slate-700">
                                      {attachments.length}
                                    </span>
                                  </div>

                                  <div className="mt-2.5 flex items-center gap-3 border-t border-slate-100 pt-2">
                                    {items.map((item) => (
                                      <div
                                        key={item.label}
                                        className="flex items-center gap-1.5"
                                        title={`${item.count} ${item.label}`}
                                      >
                                        {item.icon({ className: `text-xs ${item.color}` })}
                                        <span className="text-[11px] font-semibold text-slate-600">
                                          {item.count}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Mobile attachment breakdown */}
                              <div className="flex flex-wrap gap-2 md:hidden">
                                {items.map((item) => (
                                  <div
                                    key={item.label}
                                    className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-100 px-2.5 py-2 ${item.bg} ${item.color}`}
                                  >
                                    {item.icon({ className: "text-sm" })}
                                    <span className="text-xs font-semibold">{item.count}</span>
                                    <span className="text-[11px] font-medium">{item.label}</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          );
                        })()
                      ) : (
                        <div className="inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-slate-400">
                          {FaPaperclip({ className: "text-xs" })}
                          <span className="text-xs font-medium">No attachments</span>
                        </div>
                      )}
                    </div>


                    {/* INFO */}
                    <div className="order-1 min-w-0 flex-1 md:order-2">

                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <ProfileAvatar
                            photoUrl={report?.user?.photoUrl}
                            firstName={report?.user?.firstName}
                            size="h-10 w-10"
                          />
                          <div>

                            <p className="text-sm font-bold text-slate-900">
                              <span className="capitalize">{report?.user?.firstName} {report?.user?.lastName}</span>
                            </p>

                            <p className={`text-xs text-slate-400 ${report.user?.portfolio.toLocaleUpperCase().trim() === "ceo" ? "uppercase" : "capitalize"}`}>
                              {report?.user?.portfolio}
                            </p>

                          </div>

                        </div>

                        <span className="whitespace-nowrap text-xs text-slate-400">
                          {formatDateTime(report?.createdAt)}
                        </span>
                      </div>

                      <div className="mt-4">
                        <span className="rounded-lg bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-600 capitalize">
                          {report?.site?.name}
                        </span>

                        <h3 className="mt-3 text-base font-bold text-slate-900">
                          Progress Update
                        </h3>

                        <p className="mt-1 line-clamp-2 text-sm leading-6 text-slate-500">
                          {report?.description}
                        </p>
                      </div>

                      <div className="mt-4 flex items-center gap-4 text-xs text-slate-400">
                        {getFile("image", report.attachments).length > 0 && (
                          <span className="flex items-center gap-1.5">
                            {FaImage({})}
                            {getFile("image", report.attachments).length} photos
                          </span>
                        )}

                        {getFile("video", report.attachments).length > 0 && (
                          <span className="flex items-center gap-1.5">
                            {FaVideo({})}
                            {getFile("video", report.attachments).length} videos
                          </span>
                        )}

                        {getFile("pdf", report.attachments).length > 0 && (
                          <span className="flex items-center gap-1.5">
                            {FaFilePdf({})}
                            {getFile("pdf", report.attachments).length} pdf
                          </span>
                        )}

                        {getFile("excel", report.attachments).length > 0 && (
                          <span className="flex items-center gap-1.5">
                            {FaFileExcel({})}
                            {getFile("excel", report.attachments).length} excel
                          </span>
                        )}

                        {getFile("docs", report.attachments).length > 0 && (
                          <span className="flex items-center gap-1.5">
                            {FaFileWord({})}
                            {getFile("docs", report.attachments).length} docs
                          </span>)}

                        <span className="ml-auto flex items-center gap-1 font-semibold text-blue-600">
                          View Report
                          {FaChevronRight({ className: "text-[9px]" })}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}


              {/* PAGINATION BUTTON */}
              {filteredReports.length > itemsPerPage && <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={filteredReports.length}
                itemsPerPage={itemsPerPage}
                itemLabel="students"
                visiblePages={3}
                hasPreviousPage={page > 1}
                hasNextPage={page < totalPages}
                onPrevious={() => setPage((prev) => prev - 1)}
                onNext={() => setPage((prev) => prev + 1)}
                onPageChange={setPage}
              />}


            </div>

            {filteredReports.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center">

                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                  {FaSearch({})}
                </div>

                <h3 className="mt-4 font-bold text-slate-800">
                  No reports found
                </h3>

                <p className="mt-1 text-sm text-slate-400">
                  Try changing your search or filters.
                </p>

              </div>
            )}

          </div>
        )}


      </main>

      {/* MEDIA VIEWER */}
      <FilePreviewModal
        file={null}
        previewUrl={viewerMedia?.url}
        onClose={() => setViewerMedia({} as Attachment)}
      />

      {/* {viewerMedia && (
        <div
          onClick={() => setViewerMedia(null)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4"
        >

          <button
            onClick={() => setViewerMedia(null)}
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
          >
            {FaTimes({})}
          </button>

          <div
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[95vw]"
          >

            {viewerMedia?.type === "image" ? (
              <img
                src={fileUrl(viewerMedia?.file)}
                alt={viewerMedia?.name || "Image"}
                className="max-h-[90vh] max-w-[95vw] rounded-xl object-contain"
              />
            ) : viewerMedia?.type === "video" ? (
              <video
                src={fileUrl(viewerMedia?.file)}
                controls
                autoPlay
                className="max-h-[90vh] max-w-[95vw] rounded-xl"
              />
            ) : viewerMedia?.type?.includes("pdf") ? (
              <iframe
                src={viewerMedia.url}
                title={viewerMedia?.name || "PDF Document"}
                className="h-[90vh] w-[90vw] rounded-xl bg-white"
              />
            ) : viewerMedia?.type === "docs" ? (
              <div className="flex max-h-[90vh] max-w-[95vw] flex-col items-center justify-center rounded-xl bg-white p-10 text-center">
                <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                  {FaFileWord({ className: "text-4xl" })}
                </div>

                <h3 className="max-w-md truncate text-lg font-semibold text-slate-800">
                  {viewerMedia?.name}
                </h3>

                <p className="mt-2 text-sm text-slate-500">
                  Word document
                </p>

                <a
                  href={fileUrl(viewerMedia.file)}
                  download={viewerMedia?.file.name}
                  className="mt-6 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  Download Document
                </a>
              </div>
            ) : viewerMedia?.type === "excel" ? (
              <div className="flex max-h-[90vh] max-w-[95vw] flex-col items-center justify-center rounded-xl bg-white p-10 text-center">
                <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                  {FaFileExcel({ className: "text-4xl" })}
                </div>

                <h3 className="max-w-md truncate text-lg font-semibold text-slate-800">
                  {viewerMedia?.name}
                </h3>

                <p className="mt-2 text-sm text-slate-500">
                  Excel spreadsheet
                </p>

                <a
                  href={fileUrl(viewerMedia.file)}
                  download={viewerMedia?.name}
                  className="mt-6 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  Download Spreadsheet
                </a>
              </div>
            ) : null}

          </div>

        </div>
      )} */}

    </div>
  );
};
